import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

export function HoverInfoIcon({ text, ariaLabel = "Parameter info" }: { text: string; ariaLabel?: string }) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const bubbleRef = useRef<HTMLSpanElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const bubble = bubbleRef.current;
        if (!trigger || !bubble) return;
        const margin = 8;
        const triggerRect = trigger.getBoundingClientRect();
        const bubbleRect = bubble.getBoundingClientRect();

        let left = triggerRect.right + 8;
        let top = triggerRect.top - 6;

        if (left + bubbleRect.width > window.innerWidth - margin) {
            left = triggerRect.left - bubbleRect.width - 8;
        }
        if (left < margin) left = margin;

        if (top + bubbleRect.height > window.innerHeight - margin) {
            top = window.innerHeight - margin - bubbleRect.height;
        }
        if (top < margin) top = margin;

        setPos({ top, left });
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        updatePosition();
        const onWindowChange = () => updatePosition();
        window.addEventListener("resize", onWindowChange);
        window.addEventListener("scroll", onWindowChange, true);
        return () => {
            window.removeEventListener("resize", onWindowChange);
            window.removeEventListener("scroll", onWindowChange, true);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;
        const id = window.requestAnimationFrame(updatePosition);
        return () => window.cancelAnimationFrame(id);
    }, [open, updatePosition]);

    return (
        <span style={{ position: "relative", display: "inline-flex" }}>
            <button
                ref={triggerRef}
                type="button"
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onClick={(e) => e.preventDefault()}
                aria-label={ariaLabel}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    color: "var(--text-muted, #a5a5a5)",
                    cursor: "help",
                }}
            >
                <Info size={14} />
            </button>
            {open &&
                createPortal(
                    <span
                        ref={bubbleRef}
                        role="tooltip"
                        style={{
                            position: "fixed",
                            top: pos?.top ?? -9999,
                            left: pos?.left ?? -9999,
                            zIndex: 10050,
                            width: "220px",
                            padding: "6px 8px",
                            borderRadius: "6px",
                            background: "rgba(24, 24, 24, 0.96)",
                            color: "#f5f5f5",
                            fontSize: "12px",
                            lineHeight: 1.35,
                            boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
                            pointerEvents: "none",
                        }}
                    >
                        {text}
                    </span>,
                    document.body,
                )}
            {open && !pos && (
                <span style={{ position: "fixed", left: -9999, top: -9999 }}>
                    {/* placeholder keeps first measurement stable */}
                </span>
            )}
        </span>
    );
}
